import { API } from 'aws-amplify';
import * as Sentry from '@sentry/react';
import { hasUserSession } from 'utils/session';

type GraphQLError = {
  message?: string;
  errorType?: string;
  path?: ReadonlyArray<string | number>;
  locations?: ReadonlyArray<{ line: number; column: number }>;
};

// The single argument every caller passes, built by graphqlOperation(query, variables)
type GraphQLOptions = {
  query?: unknown;
  variables?: Record<string, unknown>;
};

type ObserverLike = {
  next: (value: unknown) => void;
  error: (err: unknown) => void;
  complete: () => void;
};

type ObservableLike = {
  subscribe: (observer: ObserverLike) => { unsubscribe: () => void };
};

type GraphqlFn = typeof API.graphql;

// The original is kept on the patched function, not in module state, so a copy of this module
// re-evaluated by hot reload still sees the interceptor as installed
type PatchedGraphqlFn = GraphqlFn & { __originalGraphql?: GraphqlFn };

// 'response': AppSync answered with errors (resolver, auth or validation)
// 'http':     AppSync answered with a non-2xx status (expired API key, 5xx); only the status code survives
// 'network':  the request never got an answer (offline, timeout, aborted)
// 'client':   Amplify threw before sending (unparseable query, missing credentials)
type ErrorKind = 'response' | 'http' | 'network' | 'client';

type CallContext = {
  operation: string;
  variables: unknown;
  // Captured synchronously so Sentry shows the caller, not the interceptor
  callSite: Error;
};

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

// Keys are split into words (camelCase, snake_case, kebab-case) and redacted when any word matches
const SENSITIVE_WORDS = new Set([
  'password',
  'passwd',
  'secret',
  'token',
  'auth',
  'authorization',
  'cookie',
  'key',
  'apikey',
  'credential',
  'credentials',
  'signature',
  'email',
  'nickname',
]);
// In variables, a string is kept only when its key ends in one of these; every other string is user data
const STRUCTURAL_WORDS = new Set([
  'id',
  'ids',
  'slug',
  'type',
  'status',
  'sort',
  'cursor',
  'date',
  'at',
]);
const MAX_STRING_LENGTH = 200; // long values add little to triage and are where user data ends up
const BASE_BACKOFF_MS = 30_000; // a repeat of the same failure is suppressed for at least this long
const MAX_BACKOFF_MS = 10 * 60_000; // doubling up to this, so a poll failing all hour reports ~10 times
const MAX_TRACKED_ERRORS = 200;
const DEBUG = process.env.NODE_ENV !== 'production';
// Expected for visitors without a session (guard-exempt pages). With a session cookie the backend fails
// differently, so this message there means the auth headers did not arrive and must still be reported
const MISSING_USER_MESSAGE = /AuthWrapper cannot find a userId/;
const OPERATION_NAME = /^\s*(?:query|mutation|subscription)\s+(\w+)/m;

/* ------------------------------------------------------------------ */
/*  Utils                                                              */
/* ------------------------------------------------------------------ */

const isObservable = (x: unknown): x is ObservableLike =>
  !!x && typeof (x as { subscribe?: unknown }).subscribe === 'function';

const isPromise = (x: unknown): x is Promise<unknown> =>
  !!x && typeof (x as { then?: unknown }).then === 'function';

const getOperationName = (query: unknown): string => {
  if (typeof query === 'string') return OPERATION_NAME.exec(query)?.[1] ?? 'unknown';

  // Parsed DocumentNode (e.g. from graphql-tag)
  const definitions = (
    query as { definitions?: Array<{ kind?: string; name?: { value?: string } }> }
  )?.definitions;
  return definitions?.find((d) => d.kind === 'OperationDefinition')?.name?.value ?? 'unknown';
};

const keyWords = (key: string): string[] =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/);

const isSensitiveKey = (key: string): boolean =>
  keyWords(key).some((word) => SENSITIVE_WORDS.has(word));

const isStructuralKey = (key: string): boolean => {
  const words = keyWords(key);
  return STRUCTURAL_WORDS.has(words[words.length - 1]);
};

const truncate = (value: string): string =>
  value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value;

// `inVariables` applies the structural-key allowlist; error details keep their message for triage.
// `allowString` is decided per key and inherited by arrays, so a list under a non-structural key is redacted too
const sanitize = (
  value: unknown,
  ancestors: object[] = [],
  inVariables = false,
  allowString = !inVariables
): unknown => {
  if (typeof value === 'string') return allowString ? truncate(value) : '[REDACTED]';
  if (value === null || typeof value !== 'object') return value;
  // Only true cycles; an object referenced twice side by side is serialized both times
  if (ancestors.includes(value)) return '[Circular]';

  // Primitive wrappers
  if (value instanceof Date) return value.toISOString();
  if (value instanceof RegExp) return value.toString();

  // Opaque binary / large objects (File extends Blob)
  if (
    value instanceof Map ||
    value instanceof Set ||
    (typeof Blob !== 'undefined' && value instanceof Blob)
  ) {
    return `[${value.constructor.name}]`;
  }

  const path = ancestors.concat([value]);

  if (Array.isArray(value)) {
    return value.map((v) => sanitize(v, path, inVariables, allowString));
  }

  const obj: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    obj[k] = isSensitiveKey(k)
      ? '[REDACTED]'
      : sanitize(v, path, inVariables, !inVariables || isStructuralKey(k));
  }
  return obj;
};

/* ------------------------------------------------------------------ */
/*  Local duplicate throttling                                         */
/* ------------------------------------------------------------------ */

type Throttle = { lastReportedAt: number; lastSeenAt: number; delay: number };
const throttles = new Map<string, Throttle>();

const shouldSend = (fingerprint: string): boolean => {
  const now = Date.now();
  const entry = throttles.get(fingerprint);
  // Quiet for a full MAX_BACKOFF_MS since the last occurrence, reported or not, starts the backoff over
  const quiet = !entry || now - entry.lastSeenAt >= MAX_BACKOFF_MS;

  if (entry && !quiet && now - entry.lastReportedAt < entry.delay) {
    entry.lastSeenAt = now;
    return false;
  }

  const delay = entry && !quiet ? Math.min(entry.delay * 2, MAX_BACKOFF_MS) : BASE_BACKOFF_MS;
  throttles.set(fingerprint, { lastReportedAt: now, lastSeenAt: now, delay });

  // Signatures that embed ids would otherwise grow the map for the life of the tab
  if (throttles.size > MAX_TRACKED_ERRORS) {
    throttles.forEach((item, key) => {
      if (now - item.lastSeenAt >= MAX_BACKOFF_MS) throttles.delete(key);
    });
  }
  return true;
};

/* ------------------------------------------------------------------ */
/*  Core reporter                                                      */
/* ------------------------------------------------------------------ */

// Failures sent to Sentry or kept as a breadcrumb, so the unhandled-rejection handler skips them
const reportedFailures = new WeakSet<object>();

const markReported = (err: unknown): void => {
  if (typeof err === 'object' && err !== null) reportedFailures.add(err);
};

/**
 * True when the interceptor already reported this failure. Used by Sentry's `beforeSend`
 * to drop the duplicate event for callers that don't catch the rejection.
 */
export const isReportedGraphqlFailure = (err: unknown): boolean =>
  typeof err === 'object' && err !== null && reportedFailures.has(err);

const HTTP_STATUS = /status code (\d{3})/;

// Tokens with a digit are mostly list indexes and ids (numeric or UUID), so replacing them groups
// the "[3]" and "[7]" variants of one error, and "Class not found - <uuid>" across classes
const normalizeMessage = (message?: unknown): string =>
  String(message ?? 'Unknown error').replace(/[\w-]*\d[\w-]*/g, '#');

// Amplify turns transport failures and non-2xx responses alike into a bare GraphQLError with axios's message
const classify = (e: GraphQLError): { kind: ErrorKind; signature: string } => {
  if (e?.errorType || e?.path || e?.locations) {
    // The main Lambda falls back to "Mutation: submitQuiz" as errorType for a plain Error, so the
    // message is what tells apart different failures of one operation
    const signature = [e.errorType, normalizeMessage(e.message)].filter(Boolean).join(': ');
    return { kind: 'response', signature };
  }
  const status = HTTP_STATUS.exec(e?.message ?? '')?.[1];
  if (status) return { kind: 'http', signature: `HTTP ${status}` };
  return { kind: 'network', signature: normalizeMessage(e?.message) };
};

const capture = (
  kind: ErrorKind,
  signature: string,
  errors: GraphQLError[],
  ctx: CallContext
): void => {
  const message = errors[0]?.message ?? 'Unknown error';

  // No response at all is the user's connection, not our bug: context for later events, not one
  if (kind === 'network') {
    Sentry.addBreadcrumb({
      category: 'graphql',
      level: 'warning',
      message: `GraphQL network error in ${ctx.operation}: ${message}`,
    });
    return;
  }

  if (MISSING_USER_MESSAGE.test(String(errors[0]?.message)) && !hasUserSession()) {
    Sentry.addBreadcrumb({
      category: 'graphql',
      level: 'info',
      message: `GraphQL ${ctx.operation}: no resolvable user`,
    });
    return;
  }

  if (!shouldSend(`graphql:${ctx.operation}:${kind}:${signature}`)) return;

  const wrapped = new Error(`GraphQL ${kind} error in ${ctx.operation}: ${message}`);
  if (ctx.callSite.stack) {
    // V8 stacks start with an "Error" header line; other engines list frames only
    wrapped.stack = ctx.callSite.stack.replace(/^Error\n/, () => `Error: ${wrapped.message}\n`);
  }

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.error('[GraphQL]', wrapped);
  }

  Sentry.withScope((scope) => {
    scope.setFingerprint(['graphql', ctx.operation, kind, signature]);
    scope.setTag('graphql.operation', ctx.operation);
    scope.setTag('graphql.kind', kind);
    if (kind === 'http') scope.setTag('graphql.http_status', signature.replace('HTTP ', ''));
    scope.setExtra('variables', sanitize(ctx.variables, [], true));
    scope.setExtra('errorCount', errors.length);
    scope.setExtra('errors', sanitize(errors));
    Sentry.captureException(wrapped);
  });
};

const reportFailure = (err: unknown, ctx: CallContext): void => {
  if (isReportedGraphqlFailure(err)) return;
  markReported(err);

  // Reporting must never change what the caller sees, so a bug in here is logged and swallowed
  try {
    // Amplify rejects with the whole { data, errors } response whenever errors is non-empty
    const errors = (err as { errors?: unknown })?.errors;
    if (Array.isArray(errors) && errors.length) {
      // One event per failed call: AppSync returns one error per list item when a single field is bad
      const { kind, signature } = classify(errors[0]);
      capture(kind, signature, errors, ctx);
      return;
    }

    const message = err instanceof Error ? err.message : String(err);
    capture('client', normalizeMessage(message), [{ message }], ctx);
  } catch (reportingError) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.error('[GraphQL] failed to report a failure', reportingError);
    }
  }
};

const interceptObservable = (source: ObservableLike, ctx: CallContext): unknown => {
  // Rebuild with the source's own Observable class (zen-observable in Amplify v2) so consumers
  // keep its full API: subscribe(next, error, complete), map, filter, closed, ...
  const ObservableCtor = source.constructor as new (
    subscriber: (observer: ObserverLike) => () => void
  ) => unknown;

  return new ObservableCtor((observer) => {
    const subscription = source.subscribe({
      // PubSub wraps every message as { provider, value } and every failure as { provider, error }
      next: (message) => {
        const payload = (message as { value?: { errors?: unknown } })?.value;
        if (Array.isArray(payload?.errors) && payload.errors.length) {
          reportFailure(payload, ctx);
        }
        observer.next(message);
      },
      error: (failure) => {
        reportFailure((failure as { error?: unknown })?.error ?? failure, ctx);
        markReported(failure);
        observer.error(failure);
      },
      complete: () => observer.complete(),
    });
    return () => subscription.unsubscribe();
  });
};

/* ------------------------------------------------------------------ */
/*  Interceptor install / restore                                      */
/* ------------------------------------------------------------------ */

/**
 * Install exactly once. Safe to call repeatedly, including after hot reload.
 */
export const installGraphqlInterceptor = (): void => {
  const original = API.graphql as PatchedGraphqlFn;
  if (original.__originalGraphql) return;

  const patched: PatchedGraphqlFn = function patchedGraphql(
    this: unknown,
    ...args: Parameters<GraphqlFn>
  ) {
    const [options] = args as unknown as [GraphQLOptions | undefined];
    const callSite = new Error();
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(callSite, patchedGraphql);
    }
    const ctx: CallContext = {
      operation: getOperationName(options?.query),
      variables: options?.variables,
      callSite,
    };

    let result: ReturnType<GraphqlFn>;
    try {
      result = original.apply(this, args);
    } catch (err) {
      // Amplify throws synchronously for unparseable queries and unknown operation types
      reportFailure(err, ctx);
      throw err;
    }

    // ------------ Subscriptions (Observable) ------------------------
    if (isObservable(result)) {
      return interceptObservable(result, ctx) as ReturnType<GraphqlFn>;
    }

    // ------------ Queries / Mutations (Promise) ---------------------
    if (isPromise(result)) {
      return result.then(undefined, (err: unknown) => {
        reportFailure(err, ctx);
        throw err;
      }) as ReturnType<GraphqlFn>;
    }

    // Fallback – unknown type, just forward
    return result;
  };
  patched.__originalGraphql = original;

  API.graphql = patched;
};

/**
 * Restore original implementation – useful in tests.
 */
export const restoreGraphqlInterceptor = (): void => {
  const original = (API.graphql as PatchedGraphqlFn).__originalGraphql;
  if (original) API.graphql = original;
};
